import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { Tooltip } from "@mui/material";
import { AnimatePresence, LayoutGroup, motion } from "framer-motion";
import Lottie from "lottie-react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import DeadEnd from "../../../../../../assets/lotties/dead-end.json";
import Empty from "../../../../../../assets/lotties/empty.json";
import Error from "../../../../../../assets/lotties/error.json";
import Network from "../../../../../../assets/lotties/network.json";
import TechAssistant from "../../../../../../assets/lotties/tech-assistant.json";
import IAHelpButton from "../../../../../../components/IaHelpButton/IaHelpButton.tsx";
import LoadingIa from "../../../../../../components/LoadingIa/LoadingIa.tsx";
import { useEmbarqueDependencias } from "../../../../../../contexts/EmbarqueDependenciasContext.tsx";
import { useProfile } from "../../../../../../contexts/ProfileContext.tsx";
import { useToast } from "../../../../../../contexts/ToastContext.tsx";
import Icon from "../../../../../../core/components/Icon.tsx";
import IconButton from "../../../../../../core/components/IconButton.tsx";
import Separator from "../../../../../../core/components/Separator.tsx";
import InputField from "../../../../../../core/data-entry/InputField.tsx";
import SelectInputField from "../../../../../../core/data-entry/SelectInputField.tsx";
import Modal from "../../../../../../core/feedback/Modal.tsx";
import useService from "../../../../../../hooks/useService.ts";
import { RetornoPedidoDTO } from "../../../../../../models/RetornoPedidoDTO.ts";
import { TipoCarroceria } from "../../../../../../models/TipoCarroceria.ts";
import { getDetalhesVeiculo, postEncontrarVeiculo, VeiculoResponse } from "../../../../../../services/InteligenciaArtificial/InteligenciaArtificial.ts";
import "./ModalEncontrarVeiculoIa.css";

const DELAY = 5000;

const versaoDescricao: Record<number, string> = {
    1: "Até 50 km, últimos 3 dias.",
    2: "Até 75 km, últimos 5 dias.",
    3: "Até 100 km, últimos 7 dias.",
    4: "Recomendação por viagens semelhantes (proximidade e disponibilidade).",
};

enum StateKey {
    INITIAL = "initial",
    LOADING = "loading",
    RESULTS = "results",
    EMPTY = "empty",
    ERROR = "error",
    END_OF_SEARCH = "end_of_search",
}

interface ModalEncontrarVeiculoIaProps {
    open: boolean;
    onClose: (idVeiculo?: number | null) => void;
    pedidoId?: number;
    previsaoEmbarque?: Date | string;
    placaCavaloAtual?: string;
}

function ModalEncontrarVeiculoIa({ open, onClose, pedidoId, previsaoEmbarque, placaCavaloAtual }: ModalEncontrarVeiculoIaProps) {
    const { profile } = useProfile();
    const { tipoCarroceria, pedido } = useEmbarqueDependencias();
    const { showToast } = useToast();

    const pedidoMemo = useMemo<RetornoPedidoDTO | undefined>(() => {
        return pedidoId ? pedido?.find(p => p.idPedido === pedidoId) : undefined;
    }, [pedidoId, pedido]);

    const [tipoCarroceriaSelecionada, setTipoCarroceriaSelecionada] = useState<{ title: string, obj: TipoCarroceria } | null>(null);
    const [eixos, setEixos] = useState<number | null>(null);
    const [versao, setVersao] = useState<number>(1);
    const [veiculosBuscados, setVeiculosBuscados] = useState<VeiculoResponse[] | null>(null);
    const [veiculoDetalhes, setVeiculoDetalhes] = useState<Record<number, string | null>>({});
    const [detalheEmBuscaId, setDetalheEmBuscaId] = useState<number | null>(null);
    const [viewState, setViewState] = useState<StateKey>(StateKey.INITIAL);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [inputsChanged, setInputsChanged] = useState<boolean>(false);

    const hasBuscadoVeiculos = useRef(false);
    const lastPedidoId = useRef<number | undefined>(pedidoId);
    const versaoRef = useRef<number>(1);
    const timeoutsRef = useRef<number[]>([]);
    const detalheEmBuscaIdRef = useRef<number | null>(detalheEmBuscaId);

    const scheduleTimeout = useCallback((fn: () => void, delay = DELAY) => {
        const id = window.setTimeout(fn, delay);
        timeoutsRef.current.push(id);
        return id;
    }, []);

    const clearPendingTimeouts = useCallback(() => {
        timeoutsRef.current.forEach((id) => clearTimeout(id));
        timeoutsRef.current = [];
    }, []);

    const [isLoadingEncontrarVeiculo, encontrarVeiculo] = useService(
        postEncontrarVeiculo, {
        autoStart: false,
        onData: (response) => {
            setVeiculoDetalhes({});
            setDetalheEmBuscaId(null);
            const uniqueVeiculos = Array.from(new Map(response.data.veiculos.map(v => [v.placa, v])).values());
            const hasCurrentPlaca = placaCavaloAtual && uniqueVeiculos.some(v => v.placa === placaCavaloAtual);
            scheduleTimeout(() => {
                if (hasCurrentPlaca) {
                    response.data.veiculos = uniqueVeiculos.filter(v => v.placa !== placaCavaloAtual);
                }
                setVeiculosBuscados(uniqueVeiculos);
                setViewState(uniqueVeiculos.length ? StateKey.RESULTS : StateKey.EMPTY);
            }, DELAY);
        },
        onError: (error) => {
            setViewState(StateKey.LOADING)
            if (error?.response?.data === "Não foi encontrado nenhum veículo.") {
                if (versaoRef.current === 4) {
                    scheduleTimeout(() => {
                        setViewState(StateKey.END_OF_SEARCH);
                        showToast("Você atingiu o fim das buscas disponíveis.", "info");
                        setVeiculoDetalhes({});
                    }, DELAY);
                    return;
                }
                scheduleTimeout(() => {
                    handleBuscarVeiculo((versaoRef.current ?? 1) + 1);
                    showToast("Nenhum veículo encontrado. Expandindo os critérios de busca.", "info");
                }, DELAY);
                return;
            }
            scheduleTimeout(() => {
                setViewState(StateKey.ERROR);
                showToast(error?.response?.data ?? "Erro realizar busca", "error");
                setErrorMessage(error?.response?.data ?? "Erro ao buscar veículos");
            }, DELAY);
        }
    });

    const [isLoadingDetalhesVeiculo, buscarDetalhesVeiculo] = useService(
        getDetalhesVeiculo, {
        autoStart: false,
        onData: (response) => {
            if (detalheEmBuscaIdRef.current) {
                setVeiculoDetalhes((prev) => ({
                    ...prev,
                    [detalheEmBuscaIdRef.current as number]: response.data.resposta || null,
                }));
            }
            setDetalheEmBuscaId(null);
        },
        onError: (error) => {
            setDetalheEmBuscaId(null);
            showToast(error?.response?.data ?? "Erro ao buscar detalhes do veículo", "error");
        }
    });

    const handleBuscarVeiculo = useCallback((versionOverride?: number) => {
        if (pedidoMemo?.idPedido && profile?.token) {
            hasBuscadoVeiculos.current = true;
            const versaoAlvoBase = typeof versionOverride === "number" ? versionOverride : versao ?? 1;
            if (versaoAlvoBase > 4) {
                setViewState(StateKey.LOADING);
                scheduleTimeout(() => {
                    setViewState(StateKey.END_OF_SEARCH);
                    showToast("Você atingiu o fim das buscas disponíveis.", "info");
                    setVeiculoDetalhes({});
                    setDetalheEmBuscaId(null);
                }, DELAY);
                return;
            }
            setVersao(versaoAlvoBase);
            versaoRef.current = versaoAlvoBase;
            encontrarVeiculo({
                token: profile?.token,
                payload: {
                    eixos: eixos,
                    pedidoId: pedidoMemo?.idPedido || 0,
                    destinatarioId: pedidoMemo?.destinatarioId,
                    tipoCarroceriaId: tipoCarroceriaSelecionada?.obj.id || null,
                    previsaoEmbarque: new Date(previsaoEmbarque || ""),
                    versao: versaoAlvoBase,
                },
            });
            setInputsChanged(false);
        }
    }, [encontrarVeiculo, scheduleTimeout, showToast, previsaoEmbarque, tipoCarroceriaSelecionada?.obj.id, pedidoMemo?.idPedido, pedidoMemo?.destinatarioId, profile?.token, eixos, versao]);

    const handleSearchIaButton = useCallback(() => {
        handleBuscarVeiculo(!inputsChanged && viewState !== StateKey.INITIAL ? versao + 1 : 1);
    }, [handleBuscarVeiculo, versao, viewState, inputsChanged]);

    const handleClearModalState = useCallback(() => {
        setTipoCarroceriaSelecionada(null);
        setEixos(null);
        setVersao(1);
        setVeiculosBuscados(null);
        setVeiculoDetalhes({});
        setDetalheEmBuscaId(null);
        setViewState(StateKey.INITIAL);
        hasBuscadoVeiculos.current = false;
        setInputsChanged(false);
        versaoRef.current = 1;
    }, []);

    const abrirWhats = useCallback((phoneNumber: string) => {
        const cleanedNumber = phoneNumber.replace(/\D/g, "");
        const formattedNumber = cleanedNumber.startsWith("55")
            ? cleanedNumber
            : `55${cleanedNumber}`;
        const url = `https://wa.me/${formattedNumber}`;
        window.open(url, "_blank");
    }, []);

    const handleDetalhesVeiculo = useCallback((idVeiculo: number) => {
        if (idVeiculo && profile?.token) {
            setDetalheEmBuscaId(idVeiculo);
            buscarDetalhesVeiculo({ token: profile?.token, idVeiculo, versao: versao ?? 1 });
        }
    }, [buscarDetalhesVeiculo, profile?.token, versao]);

    const handleRecusarVeiculo = useCallback((idVeiculo: number) => {
        if (versao === 4 && veiculosBuscados && veiculosBuscados.length <= 1) {
            setViewState(StateKey.LOADING)
            scheduleTimeout(() => {
                setViewState(StateKey.END_OF_SEARCH);
                showToast("Você atingiu o fim das buscas disponíveis.", "info");
                setVeiculoDetalhes({});
                setDetalheEmBuscaId(null);
            }, DELAY);
            return;
        }
        setVeiculosBuscados((prev) => {
            if (!prev || !prev.length) return prev;
            const next = prev.filter(v => v.idVeiculo !== idVeiculo);
            if (!next.length) {
                let versaoAtual = versao;
                if (versaoAtual > 1 || (viewState !== StateKey.INITIAL && !inputsChanged)) {
                    versaoAtual += 1;
                }
                setVersao(versaoAtual);
                handleBuscarVeiculo(versaoAtual);
            }
            return next;
        });
        setVeiculoDetalhes((prev) => {
            const { [idVeiculo]: _, ...rest } = prev;
            return rest;
        });
        if (detalheEmBuscaId === idVeiculo) setDetalheEmBuscaId(null);
    }, [handleBuscarVeiculo, showToast, viewState, versao, scheduleTimeout, inputsChanged, detalheEmBuscaId]);

    const handleAceitarVeiculo = useCallback((idVeiculo: number) => {
        const veiculo = veiculosBuscados?.find(v => v.idVeiculo === idVeiculo);
        if (!veiculo) return;
        showToast(`Veículo ${veiculo.placa} selecionado.`, "success");
        onClose(idVeiculo);
        handleClearModalState();
    }, [showToast, veiculosBuscados, onClose, handleClearModalState]);

    const handleClose = useCallback(() => {
        clearPendingTimeouts();
        onClose();
        handleClearModalState();
    }, [onClose, handleClearModalState, clearPendingTimeouts]);

    const tipoCarroceriaOptions = useMemo(() => {
        return tipoCarroceria.map(
            (item) => ({
                title: item.nome,
                obj: item,
            })
        )
    }, [tipoCarroceria]);

    const tooltipRecusaLabel = useMemo(() => {
        if (veiculosBuscados && veiculosBuscados?.length > 1) {
            return "Recusar veículo";
        }
        return "Realizar outra busca";
    }, [veiculosBuscados]);

    const disabledIaButton = useMemo(() => {
        if (viewState === StateKey.END_OF_SEARCH && !inputsChanged) return true;
        return false;
    }, [viewState, inputsChanged]);

    useEffect(() => {
        if (isLoadingEncontrarVeiculo) {
            setViewState(StateKey.LOADING);
            setVeiculoDetalhes({});
            setDetalheEmBuscaId(null);
        }
    }, [isLoadingEncontrarVeiculo]);

    useEffect(() => {
        if (lastPedidoId.current !== pedidoId) {
            handleClearModalState();
            lastPedidoId.current = pedidoId;
        }
    }, [pedidoId, handleClearModalState]);

    useEffect(() => {
        versaoRef.current = versao;
    }, [versao]);

    useEffect(() => {
        return () => {
            clearPendingTimeouts();
        };
    }, [clearPendingTimeouts]);

    useEffect(() => {
        detalheEmBuscaIdRef.current = detalheEmBuscaId;
    }, [detalheEmBuscaId]);

    return (
        <Modal
            isOpen={open}
            onClose={handleClose}
            title="Encontrar veículo com IA"
            closeButton
        >
            <motion.div
                initial={{ opacity: 0, y: 50 }}
                exit={{ opacity: 0, y: 50 }}
                animate={{
                    opacity: 1,
                    y: 0,
                }}
                layout
                transition={{ duration: 0.3 }}
                onClick={(e) => e.stopPropagation()}
                className="modal-encontrar-veiculo-container">
                <motion.div layout className="modal-encontrar-veiculo-header">
                    <div className="modal-encontrar-veiculo-header-title">
                        <div style={{
                            width: 24,
                            flexShrink: 0,
                        }} />
                        <span>Encontrar veículo com IA</span>
                        <IconButton
                            icon="X"
                            size="lg"
                            borderColor="none"
                            onClick={handleClose}
                            backgroundColor="rgba(0, 0, 0, 0)"
                            theme="dark"
                        />
                    </div>
                    <Separator size="sm" />
                    <div className="modal-encontrar-veiculo-inputs">
                        <div className="modal-encontrar-veiculo-input-container">
                            <div className="modal-encontrar-veiculo-input-field">
                                <InputField
                                    type="text"
                                    label="Nº de eixos:"
                                    value={eixos ? eixos.toString() : ""}
                                    disabled={viewState === StateKey.LOADING}
                                    onChange={(e) => {
                                        setEixos(e.target.value ? parseInt(e.target.value) : null)
                                        setVersao(1);
                                        setInputsChanged(true);
                                    }}
                                />
                                <div className="select-carroceria">
                                    <SelectInputField
                                        label="Tipo de carroceria:"
                                        value={tipoCarroceriaSelecionada?.title || ""}
                                        disabled={viewState === StateKey.LOADING}
                                        onChange={(obj) => {
                                            setTipoCarroceriaSelecionada({
                                                title: obj.title,
                                                obj: obj.obj as TipoCarroceria,
                                            })
                                            setVersao(1);
                                            setInputsChanged(true);
                                        }
                                        }
                                        placeholder="Selecione o tipo:"
                                        options={tipoCarroceriaOptions}
                                        onInputChange={() => { }}
                                        onClear={() => setTipoCarroceriaSelecionada(null)}
                                    />
                                </div>
                            </div>
                            <div className="modal-encontrar-veiculo-ia-help-button">
                                <IAHelpButton disabled={disabledIaButton} fullWidth onClick={handleSearchIaButton} loading={viewState === StateKey.LOADING || !!detalheEmBuscaId}>
                                    <span>Buscar veículos com IA</span>
                                </IAHelpButton>
                            </div>
                        </div>
                    </div>
                </motion.div>
                <motion.div layout className={`modal-encontrar-veiculo-list-container ${viewState === StateKey.LOADING ? "loading-border" : ""}`}>
                    <AnimatePresence mode="wait">
                        {viewState === StateKey.INITIAL && (
                            <motion.div
                                key={StateKey.INITIAL}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.3 }}
                                className="modal-encontrar-veiculo-initial-container"
                            >
                                <span>Faça uma busca para encontrar veículos disponíveis.</span>
                                <Lottie
                                    animationData={Network}
                                    loop
                                    autoplay
                                    style={{
                                        height: 200,
                                    }}
                                />
                            </motion.div>
                        )}

                        {viewState === StateKey.LOADING && (
                            <motion.div key={StateKey.LOADING}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.3 }}
                                className="modal-encontrar-veiculo-initial-container">
                                <span><strong>Limite de busca atual:</strong></span>
                                <span>{versaoDescricao[versao]}</span>
                                <LoadingIa />
                            </motion.div>
                        )}

                        {viewState === StateKey.EMPTY && (
                            <motion.div
                                key={StateKey.EMPTY}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.3 }}
                                className="modal-encontrar-veiculo-initial-container"
                            >
                                <span>Nenhum veículo encontrado.</span>
                                <span>Clique em no botão acima para expandir a distância de busca.</span>
                                <Lottie
                                    animationData={Empty}
                                    loop
                                    autoplay
                                    style={{
                                        width: "50%"
                                    }}
                                />
                            </motion.div>
                        )}

                        {viewState === StateKey.ERROR && (
                            <motion.div
                                key={StateKey.ERROR}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.3 }}
                                className="modal-encontrar-veiculo-initial-container"
                            >
                                <span>{errorMessage}</span>
                                <Lottie
                                    animationData={Error}
                                    loop
                                    autoplay
                                />
                            </motion.div>
                        )}

                        {viewState === StateKey.END_OF_SEARCH && (
                            <motion.div
                                key={StateKey.END_OF_SEARCH}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.3 }}
                                className="modal-encontrar-veiculo-initial-container"
                            >
                                <span>Oh não,</span>
                                <span>Não conseguimos encontrar mais veículos no momento.</span>
                                <Lottie
                                    animationData={DeadEnd}
                                    loop
                                    autoplay
                                    style={{
                                        height: 300,
                                    }}
                                />
                                <span>Tente novamente mais tarde.</span>
                            </motion.div>
                        )}

                        {viewState === StateKey.RESULTS && veiculosBuscados && veiculosBuscados.length > 0 && (
                            <LayoutGroup id="veiculos-buscados">
                                <motion.div
                                    key="lista-veiculos"
                                    className="modal-encontrar-veiculo-itens"
                                    layout
                                >
                                    {veiculosBuscados.map((veiculo) => {
                                        const detalhe = veiculoDetalhes?.[veiculo.idVeiculo];
                                        const isLoadingDetalheAtual = isLoadingDetalhesVeiculo && detalheEmBuscaId === veiculo.idVeiculo;
                                        return (
                                            <motion.div
                                                key={veiculo.idVeiculo}
                                                className="modal-encontrar-veiculo-item-container"
                                                initial={{ opacity: 0, }}
                                                animate={{ opacity: 1, }}
                                                exit={{ opacity: 0, scale: 0.98 }}
                                                transition={{ duration: 0.25 }}
                                                layout
                                            >
                                                <div className="modal-encontrar-veiculo-item-row">
                                                    <div className="modal-encontrar-veiculo-item-info">
                                                        <div className="modal-encontrar-veiculo-item-info-row">
                                                            <span>
                                                                <strong>Placa:</strong>
                                                            </span>
                                                            <span>
                                                                {veiculo.placa}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <motion.div layout="position" className="modal-encontrar-veiculo-detalhe-button">
                                                        <motion.button
                                                            key={`detalhes-veiculo-button-${veiculo.idVeiculo}`}
                                                            className="aplicar-veiculo-ia-button-secondary"
                                                            initial={false}
                                                            animate={{
                                                                opacity: !detalhe ? 1 : 0,
                                                                height: !detalhe ? "auto" : 0,
                                                                width: !detalhe ? "auto" : 0,
                                                                scale: isLoadingDetalheAtual ? 1.06 : 1,
                                                                padding: !detalhe
                                                                    ? "10px"
                                                                    : 0,
                                                            }}
                                                            transition={{
                                                                duration: 0.2
                                                            }}
                                                            whileHover={{ scale: !isLoadingDetalheAtual ? 1.02 : 1 }}
                                                            whileTap={{ scale: isLoadingDetalheAtual ? 1 : 0.96 }}
                                                            disabled={isLoadingDetalheAtual || !!detalhe}
                                                            onClick={() => handleDetalhesVeiculo(veiculo.idVeiculo)}
                                                        >
                                                            <div style={{
                                                                width: 24,
                                                                height: 24,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                flexShrink: 0,
                                                            }}>
                                                                <Icon size="lg" name="Bot" theme="light" />
                                                            </div>
                                                            <span>Gerar Detalhes</span>
                                                            <motion.span
                                                                className="help-spinner"
                                                                aria-hidden
                                                                initial={{ opacity: 0, width: "8px" }}
                                                                animate={{ opacity: isLoadingDetalheAtual ? 1 : 0, width: isLoadingDetalheAtual ? "16px" : "8px" }}
                                                                transition={{ duration: 0.18 }}
                                                            />
                                                        </motion.button>
                                                    </motion.div>
                                                    <motion.div layout className="modal-encontrar-veiculo-actions">
                                                        <Tooltip title={tooltipRecusaLabel} >
                                                            <IconButton backgroundColor="#b91c1c" hoverColor="#c84141ff" onClick={() => handleRecusarVeiculo(veiculo.idVeiculo)} theme="light" icon="X" size="xl" />
                                                        </Tooltip>
                                                        <Tooltip title="Aceitar veículo" >
                                                            <IconButton backgroundColor="#15803d" hoverColor="#29a356ff" onClick={() => handleAceitarVeiculo(veiculo.idVeiculo)} theme="light" icon="Check" size="xl" />
                                                        </Tooltip>
                                                    </motion.div>
                                                </div>
                                                <div className="modal-encontrar-veiculo-item-info-container">
                                                    <motion.div
                                                        key={`detalhe-${veiculo.idVeiculo}1`}
                                                        initial={{ opacity: 0, height: 0 }}
                                                        animate={{ opacity: isLoadingDetalheAtual ? 1 : 0, height: isLoadingDetalheAtual ? 180 : 0 }}
                                                        transition={{ duration: 0.45 }}
                                                        className="modal-encontrar-veiculo-detalhe-loading"
                                                        style={{
                                                            paddingTop: isLoadingDetalheAtual ? 16 : 0,
                                                        }}
                                                    >
                                                        <span>Gerando detalhes do veículo.</span>
                                                        <Lottie
                                                            animationData={TechAssistant}
                                                            loop
                                                            autoplay
                                                            style={{
                                                                height: 150,
                                                                position: "absolute",
                                                                zIndex: 1,
                                                            }}
                                                        />
                                                    </motion.div>
                                                    <motion.div
                                                        key={`detalhe-${veiculo.idVeiculo}`}
                                                        initial={{ opacity: 0, height: 0 }}
                                                        animate={{ opacity: detalhe ? 1 : 0, height: detalhe ? "auto" : 0 }}
                                                        exit={{ opacity: 0, height: 0 }}
                                                        transition={{ duration: 0.45 }}
                                                        className="modal-encontrar-veiculo-detalhe"
                                                    >
                                                        <p key="detalhes-veiculo-texto">
                                                            {detalhe && (
                                                                <>
                                                                    <span>
                                                                        <div className="modal-encontrar-veiculo-item-zap-button">
                                                                            <strong>Telefone Motorista:</strong>
                                                                            {veiculo.telefoneMotorista}
                                                                            <Tooltip title="Contato Motorista" disableInteractive>
                                                                                <WhatsAppIcon
                                                                                    sx={{ color: "#25D366", cursor: "pointer" }} onClick={() => { abrirWhats(veiculo.telefoneMotorista); }} />
                                                                            </Tooltip>
                                                                        </div>
                                                                    </span>
                                                                    <Separator size="sm" />
                                                                    {detalhe}
                                                                </>
                                                            )}
                                                        </p>
                                                    </motion.div>
                                                </div>
                                            </motion.div>
                                        )
                                    })}
                                </motion.div>
                            </LayoutGroup>
                        )}
                    </AnimatePresence>
                </motion.div>
            </motion.div>
        </Modal >
    )
};

export default memo(ModalEncontrarVeiculoIa);